import { useState } from 'react';
import { Box, Paper, Typography, Button, Stack, CircularProgress, Alert } from '@mui/material';
import { useMe } from '../context/MeContext';
import { useAuth } from '../context/AuthContext';

/**
 * Shown instead of the portal to anyone whose clinic membership is not approved.
 * Three cases land here: waiting on an admin, declined, and "signed in but not
 * attached to any clinic" (legacy accounts created before join codes existed).
 */
const PendingApproval = () => {
  const { me, membershipStatus, refresh } = useMe();
  const { logout } = useAuth();
  const [checking, setChecking] = useState(false);

  const clinicName = me?.tenant?.name;

  const check = async () => {
    setChecking(true);
    try {
      await refresh();
    } finally {
      setChecking(false);
    }
  };

  const copy = {
    Pending: {
      severity: 'info',
      title: 'Waiting for approval',
      body: clinicName
        ? `Your request to join ${clinicName} has been sent. An administrator there needs to approve you before you can see patients and assessments.`
        : 'Your request has been sent. An administrator needs to approve you before you can see patients and assessments.',
    },
    Rejected: {
      severity: 'warning',
      title: 'Request declined',
      body: clinicName
        ? `Your request to join ${clinicName} was declined. If you think this is a mistake, contact the clinic administrator.`
        : 'Your request to join was declined. Contact the clinic administrator if you think this is a mistake.',
    },
    Deactivated: {
      severity: 'error',
      title: 'Account deactivated',
      body: clinicName
        ? `Your account at ${clinicName} has been deactivated. Contact the clinic administrator if you need access again.`
        : 'Your account has been deactivated. Contact your clinic administrator if you need access again.',
    },
    None: {
      severity: 'warning',
      title: 'No clinic yet',
      body: 'Your account is not part of a clinic. Ask a clinic administrator for their clinic code, or create a clinic of your own.',
    },
  };

  const state = copy[membershipStatus] || copy.None;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', py: 8, px: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 520, width: '100%' }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {state.title}
        </Typography>

        <Alert severity={state.severity} sx={{ mb: 3 }}>
          {state.body}
        </Alert>

        {me?.email && (
          <Typography variant="body2" color="text.secondary" mb={3}>
            Signed in as {me.email}
          </Typography>
        )}

        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={check} disabled={checking}>
            {checking ? <CircularProgress size={20} color="inherit" /> : 'Check again'}
          </Button>
          <Button onClick={logout}>Sign out</Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default PendingApproval;
